import React, { useState } from "react";
import {
    addServedState,
    updateServedState,
    deleteServedState
} from "DBServices/firestoreDBService";
import cogoToast from "cogo-toast";

const cardStyle = {
    background: "white",
    borderRadius: 12,
    padding: "1.5rem",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
};
const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    fontSize: "0.95rem"
};
const greenBtn = {
    background: "#10b981",
    color: "white",
    padding: "10px 18px",
    borderRadius: 8,
    fontWeight: 700,
    border: "none",
    cursor: "pointer",
    fontSize: "0.85rem"
};
const chipStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "#d1fae5",
    color: "#065f46",
    fontSize: "0.78rem",
    fontWeight: 600,
    padding: "4px 10px",
    borderRadius: 999
};

const parseList = (str) =>
    str.split(",").map((s) => s.trim()).filter(Boolean);

export default function ServedAreasManager({ states = [], loading, reload }) {
    const [newStateName, setNewStateName] = useState("");
    const [newNeighborhoods, setNewNeighborhoods] = useState("");
    const [addInputs, setAddInputs] = useState({});
    const [busy, setBusy] = useState(false);

    const handleAddState = async (e) => {
        e.preventDefault();
        const name = newStateName.trim();
        if (!name) {
            cogoToast.warn("Enter a state name");
            return;
        }
        setBusy(true);
        try {
            await addServedState({ name, neighborhoods: parseList(newNeighborhoods) });
            cogoToast.success(`Added ${name} with ${parseList(newNeighborhoods).length} neighborhood(s)`);
            setNewStateName("");
            setNewNeighborhoods("");
            await reload();
        } catch (err) {
            cogoToast.error("Failed to add state: " + err.message);
        } finally {
            setBusy(false);
        }
    };

    const handleAddNeighborhood = async (state, value) => {
        const nb = (value || "").trim();
        if (!nb) return;
        const existing = Array.isArray(state.neighborhoods) ? state.neighborhoods : [];
        if (existing.includes(nb)) {
            cogoToast.info("Neighborhood already exists for this state");
            return;
        }
        setBusy(true);
        try {
            await updateServedState(state.firestoreId, {
                name: state.name,
                neighborhoods: [...existing, nb]
            });
            setAddInputs((prev) => ({ ...prev, [state.firestoreId]: "" }));
            await reload();
        } catch (err) {
            cogoToast.error("Failed to add neighborhood: " + err.message);
        } finally {
            setBusy(false);
        }
    };

    const handleRemoveNeighborhood = async (state, nb) => {
        setBusy(true);
        try {
            await updateServedState(state.firestoreId, {
                name: state.name,
                neighborhoods: (state.neighborhoods || []).filter((n) => n !== nb)
            });
            await reload();
        } catch (err) {
            cogoToast.error("Failed to remove neighborhood: " + err.message);
        } finally {
            setBusy(false);
        }
    };

    const handleDeleteState = async (state) => {
        if (!window.confirm(`Delete state "${state.name}" and all its neighborhoods?`)) return;
        setBusy(true);
        try {
            await deleteServedState(state.firestoreId);
            cogoToast.success("State deleted");
            await reload();
        } catch (err) {
            cogoToast.error("Failed to delete state: " + err.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div>
            <div style={{ fontSize: "0.85rem", color: "#6b7280", marginBottom: "1rem" }}>
                Define the states your business serves and the neighborhood areas within each.
                These areas populate the State / Neighborhood selectors across the Co-Buy hub,
                the homepage buy form, and the marketplace.
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: "1.5rem", alignItems: "start" }}>
                {/* Existing states */}
                <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                    {loading ? (
                        <div style={{ textAlign: "center", padding: "3rem", color: "#6b7280" }}>
                            Loading served areas…
                        </div>
                    ) : states.length === 0 ? (
                        <div style={{ textAlign: "center", padding: "3rem", color: "#9ca3af", fontStyle: "italic", ...cardStyle }}>
                            No states added yet. Use the form on the right to add your first served state.
                        </div>
                    ) : (
                        states.map((state) => (
                            <div key={state.firestoreId} style={cardStyle}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                                    <strong style={{ fontSize: "1.05rem", color: "#111827" }}>{state.name}</strong>
                                    <button
                                        onClick={() => handleDeleteState(state)}
                                        style={{ color: "#ef4444", fontWeight: 700, border: "none", background: "none", cursor: "pointer", fontSize: "0.8rem" }}
                                    >
                                        Delete State
                                    </button>
                                </div>

                                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginBottom: "0.75rem" }}>
                                    {(state.neighborhoods || []).length === 0 && (
                                        <span style={{ color: "#9ca3af", fontStyle: "italic", fontSize: "0.8rem" }}>
                                            No neighborhoods yet
                                        </span>
                                    )}
                                    {(state.neighborhoods || []).map((nb) => (
                                        <span key={nb} style={chipStyle}>
                                            {nb}
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveNeighborhood(state, nb)}
                                                style={{ background: "none", border: "none", color: "#047857", cursor: "pointer", fontWeight: 700, lineHeight: 1, padding: 0, fontSize: "0.9rem" }}
                                                title={`Remove ${nb}`}
                                            >
                                                ×
                                            </button>
                                        </span>
                                    ))}
                                </div>

                                <div style={{ display: "flex", gap: "0.5rem" }}>
                                    <input
                                        type="text"
                                        placeholder="Add a neighborhood area"
                                        value={addInputs[state.firestoreId] || ""}
                                        onChange={(e) => setAddInputs((prev) => ({ ...prev, [state.firestoreId]: e.target.value }))}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                handleAddNeighborhood(state, addInputs[state.firestoreId]);
                                            }
                                        }}
                                        style={{ ...inputStyle, flex: 1 }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => handleAddNeighborhood(state, addInputs[state.firestoreId])}
                                        disabled={busy}
                                        style={greenBtn}
                                    >
                                        + Add
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Add new state */}
                <div style={{ ...cardStyle, position: "sticky", top: 16 }}>
                    <h3 style={{ fontWeight: 700, fontSize: "1rem", color: "#111827", marginBottom: 6 }}>➕ Add Covered State</h3>
                    <p style={{ fontSize: "0.8rem", color: "#6b7280", marginBottom: 16 }}>
                        Enter the state name and optionally a comma-separated list of neighborhood areas.
                    </p>
                    <form onSubmit={handleAddState} style={{ display: "grid", gap: 12 }}>
                        <div>
                            <label style={{ display: "block", fontWeight: 600, fontSize: "0.82rem", color: "#374151", marginBottom: 4 }}>State Name *</label>
                            <input
                                value={newStateName}
                                onChange={(e) => setNewStateName(e.target.value)}
                                placeholder="e.g. Lagos"
                                style={inputStyle}
                                required
                            />
                        </div>
                        <div>
                            <label style={{ display: "block", fontWeight: 600, fontSize: "0.82rem", color: "#374151", marginBottom: 4 }}>
                                Neighborhood Areas <span style={{ fontWeight: 400, color: "#9ca3af" }}>(comma separated, optional)</span>
                            </label>
                            <textarea
                                value={newNeighborhoods}
                                onChange={(e) => setNewNeighborhoods(e.target.value)}
                                placeholder="Ikeja, Lekki, Yaba, Gbagada, Surulere"
                                style={{ ...inputStyle, minHeight: 80 }}
                            />
                        </div>
                        <button type="submit" disabled={busy} style={{ ...greenBtn, width: "100%" }}>
                            Add State
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
